/**
 * Button motion presets — each `<MMotionButton>` instance picks one at random
 * on mount, so groups of buttons feel hand-made instead of pixel-identical.
 *
 * Brand-tuned: chunkier scales / wobble than a generic system, to match the
 * Massive Monster "every surface should feel hand-made" principle.
 */

export interface ButtonMotionPreset {
  whileHover: Record<string, any>
  whilePress: Record<string, any>
  transition: Record<string, any>
}

export const buttonMotionPresets: readonly ButtonMotionPreset[] = [
  {
    whileHover: { scale: 1.04, rotate: 0.6, y: -2 },
    whilePress: { scale: 0.96, y: 0 },
    transition: { duration: 0.15, ease: 'easeInOut' },
  },
  {
    whileHover: { scale: 1.045, rotate: -0.4, y: -1 },
    whilePress: { scale: 0.965, rotate: 0 },
    transition: { duration: 0.12, ease: 'easeOut' },
  },
  {
    whileHover: { scale: 1.035, rotate: 1, x: 0.5 },
    whilePress: { scale: 0.975, x: 0 },
    transition: { duration: 0.18, ease: 'easeInOut' },
  },
  {
    whileHover: { scale: 1.05, rotate: 0.3, y: -2.5 },
    whilePress: { scale: 0.95, rotate: 0.2 },
    transition: {
      duration: 0.14,
      ease: 'backOut',
      scale: { type: 'spring', stiffness: 320, damping: 15 },
    },
  },
  {
    whileHover: { scale: 1.03, rotate: -0.8, x: -0.5, y: -1.5 },
    whilePress: { scale: 0.985, x: 0, y: 0 },
    transition: { duration: 0.16, ease: 'anticipate' },
  },
] as const

export const namedButtonPresets = {
  /** Smallest signature — settle slightly without drawing too much attention. */
  subtle: {
    whileHover: { scale: 1.02, y: -1 },
    whilePress: { scale: 0.98 },
    transition: { duration: 0.15, ease: 'easeInOut' },
  },
  /** Springy bounce — primary CTAs that should feel alive. */
  bouncy: buttonMotionPresets[3]!,
  /** Playful tilt — used on whimsical / discord / merch CTAs. */
  playful: buttonMotionPresets[1]!,
} as const satisfies Record<string, ButtonMotionPreset>

export type NamedButtonPreset = keyof typeof namedButtonPresets

export function createButtonMotion() {
  function getRandomPreset(): ButtonMotionPreset {
    return buttonMotionPresets[Math.floor(Math.random() * buttonMotionPresets.length)]!
  }

  function getNamedPreset(name: NamedButtonPreset): ButtonMotionPreset {
    return namedButtonPresets[name]
  }

  function getPresetByIndex(index: number): ButtonMotionPreset {
    return buttonMotionPresets[index % buttonMotionPresets.length]!
  }

  return { buttonMotionPresets, namedButtonPresets, getRandomPreset, getNamedPreset, getPresetByIndex }
}
